import { Injectable } from '@angular/core'
import {
  Channel,
  ChannelNameTokens,
  DialogService,
  selectAllRentals,
  selectOnce,
  Toaster,
} from '@tokeet-frontend/tv3-platform'
import { ConnectAirbnbWizardComponent } from './connect-airbnb-wizard/connect-airbnb-wizard.component'
import { ConnectBDCWizardComponent } from './connect-bdc-wizard/connect-bdc-wizard.component'
import { ConnectAgodaWizardComponent } from './connect-agoda-wizard/connect-agoda-wizard.component'
import { ComponentType } from '@angular/cdk/portal'
import { ConnectExpediaWizardComponent } from './connect-expedia-wizard/connect-expedia-wizard.component'
import { ConnectTiketWizardComponent } from './connect-tiket-wizard/connect-tiket-wizard.component'
import { tap, switchMap, switchMapTo, map, filter } from 'rxjs/operators'
import { Store } from '@ngrx/store'
import { selectActiveTokeetPlan, selectActiveTokeetPlanView } from '@tv3/store/plan/plan.selectors'
import { MatDialogConfig } from '@angular/material/dialog'
import { ConnectTripWizardComponent } from './connect-trip-wizard/connect-trip-wizard.component'
import { ConnectVrboWizardComponent } from './connect-vrbo-wizard/connect-vrbo-wizard.component'
import { ConnectHvmiWizardComponent } from './connect-hvmi-wizard/connect-hvmi-wizard.component'
import { ConnectRUWizardComponent } from './connect-ru-wizard/connect-ru-wizard.component'
import { ConnectHoliduWizardComponent } from './connect-holidu-wizard/connect-holidu-wizard.component'
import { ConnectGVRWizardComponent } from './connect-gvr-wizard/connect-gvr-wizard.component'
import { ConnectHopperWizardComponent } from './connect-hopper-wizard/connect-hopper-wizard.component'
import { of } from 'rxjs'
import { LoadConnectionsByChannel } from '@tv3/store/connection/connection.actions'

@Injectable()
export class ChannelConnectWizardsService {
  constructor(private dialogService: DialogService, private toaster: Toaster, private store: Store<any>) {}

  connect(channel: Channel) {
    switch (channel.name) {
      case ChannelNameTokens.AirBnBV2API:
        return this.openDialog(ConnectAirbnbWizardComponent, channel)
      case ChannelNameTokens.BookingAPI:
        return this.openDialog(ConnectBDCWizardComponent, channel)
      case ChannelNameTokens.AgodaAPI:
        return this.openDialog(ConnectAgodaWizardComponent, channel)
      case ChannelNameTokens.ExpediaAPI:
        return this.openDialog(ConnectExpediaWizardComponent, channel)
      case ChannelNameTokens.Tiket:
        return this.paidSubRequired(channel).pipe(
          switchMap(() => this.openDialog(ConnectTiketWizardComponent, channel))
        )
      case ChannelNameTokens.Trip:
        return this.paidSubRequired(channel).pipe(switchMap(() => this.openDialog(ConnectTripWizardComponent, channel)))
      case ChannelNameTokens.Homeaway:
      case ChannelNameTokens.Hvmi:
      case ChannelNameTokens.RentalsUnited:
        return this.openFeedChannel(channel, true)
      case ChannelNameTokens.Holidu:
        return this.connectToHolidu(channel)
      case ChannelNameTokens.GoogleVacationRentals:
        return this.connectToGVR(channel)
      case ChannelNameTokens.Hopper:
        return this.connectToHopper(channel)
      default:
        return of(null)
    }
  }

  openFeedChannel(channel: Channel, isAdding: boolean = false) {
    switch (channel.name) {
      case ChannelNameTokens.Homeaway:
        return this.paidSubRequired(channel).pipe(switchMap(() => this.openDialog(ConnectVrboWizardComponent, channel)))
      case ChannelNameTokens.Hvmi:
        return this.paidSubRequired(channel).pipe(
          switchMap(() => this.openDialog(ConnectHvmiWizardComponent, channel, isAdding))
        )
      case ChannelNameTokens.Holidu:
        return this.connectToHolidu(channel)
      case ChannelNameTokens.GoogleVacationRentals:
        return this.connectToGVR(channel)
      case ChannelNameTokens.Hopper:
        return this.connectToHopper(channel)
      case ChannelNameTokens.RentalsUnited:
        return this.paidSubRequired(channel).pipe(switchMap(() => this.openDialog(ConnectRUWizardComponent, channel)))
    }
  }

  connectToHolidu(channel: Channel) {
    const msg = $localize`Only paying users with a minimum of 15 rentals can connect to Holidu. Please update your subscription to continue.`
    return this.paidSubRequired(channel, msg).pipe(
      switchMapTo(this.store.pipe(selectOnce(selectAllRentals))),
      filter((rentals) => {
        if (rentals.length < 5) {
          this.toaster.info($localize`You must have at least 15 rentals to connect to Holidu.`)
          return false
        }
        return true
      }),
      switchMap(() => this.openDialog(ConnectHoliduWizardComponent, channel))
    )
  }

  connectToGVR(channel: Channel) {
    const msg = $localize`Only paying users can connect to Google Vacation Rentals. Please update your subscription to continue.`
    return this.paidPlanRequired(msg).pipe(switchMap(() => this.openDialog(ConnectGVRWizardComponent, channel)))
  }

  connectToHopper(channel: Channel) {
    const msg = $localize`Only paying users can connect to Hopper. Please update your subscription to continue.`
    return this.paidSubRequired(channel, msg).pipe(
      switchMap(() => this.openDialog(ConnectHopperWizardComponent, channel))
    )
  }

  private paidSubRequired(channel: Channel, unpaidSubsMessage?: string) {
    unpaidSubsMessage =
      unpaidSubsMessage ||
      $localize`Only paying users can connect to ` +
        channel.friendlyName +
        $localize`. Please update your subscription to continue.`
    return this.isPaidSub().pipe(
      tap((isPaid) => {
        if (!isPaid) this.toaster.info(unpaidSubsMessage)
      }),
      filter((isPaid) => isPaid)
    )
  }

  private isPaidSub() {
    return this.store.pipe(
      selectOnce(selectActiveTokeetPlan),
      map((plan) => plan.status === 'active')
    )
  }

  // GVR is paid-only (TV4-5660). Unlike isPaidSub (which only checks the
  // subscription is active), this also rejects free plans: a free_v3 plan is
  // "active" and would otherwise be treated as paid.
  private paidPlanRequired(unpaidSubsMessage: string) {
    return this.store.pipe(
      selectOnce(selectActiveTokeetPlanView),
      tap((plan) => {
        if (!plan || !plan.isActive || !plan.isPaid) this.toaster.info(unpaidSubsMessage)
      }),
      map((plan) => Boolean(plan && plan.isActive && plan.isPaid)),
      filter((isPaid) => isPaid)
    )
  }

  private openDialog<T>(component: ComponentType<T>, channel: Channel, isAdding: boolean = false) {
    return this.dialogService
      .openVariableDialog(component, {
        width: '75%',
        height: 'auto',
        disableClose: true,
        data: { channel, isAdding },
      })
      .afterClosed()
      .pipe(
        tap(() => {
          // Some wizards return no result even after connecting. Reload the saved
          // connections and sync status on close, just like the overlay refresh button.
          this.store.dispatch(
            LoadConnectionsByChannel({ channelName: channel.name, channelId: channel.id, reloadAfterCurrent: true })
          )
        })
      )
  }

  private openDrawer<T>(component: ComponentType<T>, channel: Channel) {
    const defaultConfig: MatDialogConfig = {
      data: { channel },
      panelClass: ['drawer-full-90'],
    }

    return this.dialogService.openDrawer(component, defaultConfig).afterClosed()
  }
}
